import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { PublicUser } from '@pixacare/domain';
import { FlattenedSequence, FlattenedTelemonitoring, Telemonitoring } from '@pixacare/dtos';
import { PagedResponse } from '@pixacare/shared';
import { SequenceHttpService } from '@services/http/sequence.http.service';
import { TelemonitoringHttpService } from '@services/http/telemonitoring.http.service';
import { PaginationOptions } from '@shared/models/pagination/pagination-options';
import { catchError, concat, concatMap, mergeMap, Observable, of, switchMap } from 'rxjs';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { patientsActions } from '../patients/patients.actions';
import { sequencesActions } from '../sequences/sequences.actions';
import { usersActions } from '../users/users.actions';
import { telemonitoringsActions } from './telemonitorings.actions';

const navigationActions = (collection: PagedResponse<FlattenedTelemonitoring>): Action[] => [
  ...(collection.data || []).map((telemonitoring: FlattenedTelemonitoring) =>
    patientsActions.updatePatientSuccess({
      clientCode: telemonitoring.telemonitoring.clientCode,
      patient: telemonitoring.patient,
    }),
  ),
];

const handlePreviousNavigation = (
  collection: PagedResponse<FlattenedTelemonitoring>,
  options?: PaginationOptions,
): Action[] => [
  ...navigationActions(collection),
  telemonitoringsActions.getPreviousUserTelemonitoringsSuccess({
    reset: options?.reset,
    collection,
  }),
];

const handleNextNavigation = (
  collection: PagedResponse<FlattenedTelemonitoring>,
  options?: PaginationOptions,
): Action[] => [
  ...navigationActions(collection),
  telemonitoringsActions.getNextUserTelemonitoringsSuccess({
    reset: options?.reset,
    collection,
  }),
];

@Injectable()
export class TelemonitoringsEffects {
  private readonly actions$ = inject(Actions);
  private readonly telemonitoringService = inject(TelemonitoringHttpService);
  private readonly sequenceService = inject(SequenceHttpService);
  private readonly translocoService = inject(TranslocoService);

  getNextUserTelemonitorings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.getNextUserTelemonitorings),
      mergeMap(({ options: { query, reset } }) =>
        this.telemonitoringService.getTelemonitorings({ query }).pipe(
          mergeMap((collection: PagedResponse<FlattenedTelemonitoring>) =>
            handleNextNavigation(collection, { query, reset }),
          ),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  getPreviousUserTelemonitorings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.getPreviousUserTelemonitorings),
      mergeMap(({ options: { query, reset } }) =>
        this.telemonitoringService.getTelemonitorings({ query }).pipe(
          mergeMap((collection: PagedResponse<FlattenedTelemonitoring>) =>
            handlePreviousNavigation(collection, { query, reset }),
          ),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  getTelemonitoring$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.getTelemonitoring),
      mergeMap(({ telemonitoringId }) =>
        this.telemonitoringService.getFlattenedTelemonitoring(telemonitoringId).pipe(
          mergeMap((telemonitoring: FlattenedTelemonitoring) => [
            ...(telemonitoring.sequences && telemonitoring.sequences.length
              ? [
                  sequencesActions.loadSequences({
                    clientCode: telemonitoring.telemonitoring.clientCode,
                    data: telemonitoring.sequences,
                  }),
                ]
              : []),
            telemonitoringsActions.getTelemonitoringSuccess({ telemonitoring }),
            patientsActions.updatePatientSuccess({
              clientCode: telemonitoring.telemonitoring.clientCode,
              patient: telemonitoring.patient,
            }),
            usersActions.updateUsers({
              users: [telemonitoring.createdByInstance],
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate('Télésuivi introuvable.'),
              message: this.translocoService.translate(
                `Le télésuivi que vous recherchez n'existe pas ou a été supprimé.`,
              ),
            }),
          ]),
        ),
      ),
    ),
  );

  getSequences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.getSequences),
      mergeMap(({ tmId, clientCode }) =>
        this.sequenceService.getUserTelemonitoringSequences(tmId).pipe(
          mergeMap((sequences: FlattenedSequence[]) => [
            sequencesActions.loadSequences({
              clientCode,
              data: sequences,
            }),
            telemonitoringsActions.loadTelemonitoringSequences({
              tmId,
              sequences,
            }),
          ]),
          catchError((error: HttpErrorResponse) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  deleteTelemonitoring$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.deleteTelemonitoring),
      concatMap(({ telemonitoringId }) =>
        concat(
          of(
            notificationsActions.info({
              title: this.translocoService.translate('Le télésuivi est en cours de suppression.'),
            }),
          ),
          this.telemonitoringService.deleteTelemonitoring(telemonitoringId).pipe(
            concatMap(() => [
              telemonitoringsActions.deleteTelemonitoringSuccess({ telemonitoringId }),
              notificationsActions.success({
                title: this.translocoService.translate('Le télésuivi a bien été supprimée.'),
                message: this.translocoService.translate('Votre télésuivi a été supprimée avec succès.'),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate('Impossible de supprimer le télésuivi.'),
                message: this.translocoService.translate('Un problème est survenu.'),
              }),
            ]),
          ),
        ),
      ),
    ),
  );

  archiveTelemonitoring$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.archiveTelemonitoring),
      mergeMap(({ telemonitoringId }) =>
        this.telemonitoringService.archive(telemonitoringId).pipe(
          mergeMap((archivedTelemonitoring: Telemonitoring) => [
            telemonitoringsActions.archiveTelemonitoringSuccess({ telemonitoring: archivedTelemonitoring }),
            notificationsActions.success({
              title: this.translocoService.translate('Le télésuivi a bien été archivé.'),
              message: this.translocoService.translate('Télésuivi archivé'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu archiver le télésuivi."),
            }),
          ]),
        ),
      ),
    ),
  );

  unarchiveTelemonitoring$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.unarchiveTelemonitoring),
      mergeMap(({ telemonitoringId }) =>
        this.telemonitoringService.unarchive(telemonitoringId).pipe(
          mergeMap((unarchivedTelemonitoring: Telemonitoring) => [
            telemonitoringsActions.unarchiveTelemonitoringSuccess({ telemonitoring: unarchivedTelemonitoring }),
            notificationsActions.success({
              title: this.translocoService.translate('Le télésuivi a bien été désarchivé.'),
              message: this.translocoService.translate('Télésuivi désarchivé'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu désarchiver le télésuivi."),
            }),
          ]),
        ),
      ),
    ),
  );

  updateTelemonitoringLastNotifiedOn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.sendDepositReminder),
      mergeMap(({ telemonitoringId }) =>
        this.telemonitoringService.sendDepositReminder(telemonitoringId).pipe(
          switchMap((telemonitoring: Telemonitoring) => [
            telemonitoringsActions.sendDepositReminderSuccess({ telemonitoringId, telemonitoring }),
            notificationsActions.success({
              title: this.translocoService.translate('Relance effectuée avec succès.'),
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  unlinkSequence$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.unlinkSequence),
      concatMap((payload) =>
        concat(
          of(
            notificationsActions.info({
              title: this.translocoService.translate('La séquence est en cours de retrait du télésuivi.'),
            }),
          ),
          this.telemonitoringService
            .unlinkSequence(payload.telemonitoringId, payload.clientCode, payload.sequenceId)
            .pipe(
              switchMap(() => [
                sequencesActions.deleteSequenceSuccess({ sequenceId: payload.sequenceId }),
                telemonitoringsActions.unlinkSequenceSuccess(payload),
                notificationsActions.success({
                  title: this.translocoService.translate('La séquence a bien été retirée du télésuivi.'),
                  message: this.translocoService.translate('Retrait réussi'),
                }),
              ]),
              catchError((error: Error) => [
                lifeCycleActions.loadError({ error }),
                notificationsActions.error({
                  error,
                  title: this.translocoService.translate('Impossible de retirer la séquence.'),
                  message: this.translocoService.translate('Un problème est survenu.'),
                }),
              ]),
            ),
        ),
      ),
    ),
  );

  linkSequences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.linkSequences),
      concatMap(({ telemonitoringId, clientCode, sequenceIds }) =>
        concat(
          of(
            notificationsActions.info({
              title: this.translocoService.translate('Les séquences sont en cours de liaison au télésuivi.'),
            }),
          ),
          this.telemonitoringService.linkSequences(telemonitoringId, clientCode, sequenceIds).pipe(
            switchMap(() => [
              telemonitoringsActions.getSequences({
                tmId: telemonitoringId,
                clientCode,
              }),
              telemonitoringsActions.linkSequencesSuccess({ telemonitoringId, sequenceIds }),
              notificationsActions.success({
                title: this.translocoService.translate('Les séquences ont bien été liées au télésuivi.'),
                message: this.translocoService.translate('Liaison réussie'),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate('Impossible de lier les séquences.'),
                message: this.translocoService.translate('Un problème est survenu.'),
              }),
            ]),
          ),
        ),
      ),
    ),
  );

  addUsersToTelemonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.addTelemonitoringUsers),
      concatMap(({ telemonitoringId, userIds }) =>
        concat(
          of(
            notificationsActions.info({
              title: this.translocoService.translate(`Les utilisateurs sont en cours d'ajout au télésuivi.`),
            }),
          ),
          this.telemonitoringService.addTelemonitoringUsers(telemonitoringId, userIds).pipe(
            switchMap(() => [
              notificationsActions.success({
                title: this.translocoService.translate('Les utilisateurs ont bien été ajoutés au télésuivi.'),
                message: this.translocoService.translate('Ajout réussi'),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate(`Impossible d'ajouter les utilisateurs.`),
                message: this.translocoService.translate('Un problème est survenu.'),
              }),
            ]),
          ),
        ),
      ),
    ),
  );

  getTelemonitoringUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(telemonitoringsActions.getTelemonitoringUsers),
      mergeMap(({ telemonitoringId }) =>
        this.telemonitoringService.getUsers(telemonitoringId).pipe(
          switchMap((users: PublicUser[]) => [
            usersActions.updateUsers({ users }),
            telemonitoringsActions.getTelemonitoringUsersSuccess({
              telemonitoringId,
              userIds: users.map((user) => user.id),
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );
}
